import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Box } from "@mui/material";
import { useGetSingleHierarchyQuery, useGetActiveStaffQuery } from "../../../features/employeeSlice";
import { useGetAllProjectsQuery } from "../../../features/projectSlice";
import { useGetCMPClientsQuery } from "../../../features/cmpSlice";
import { toast, Toaster } from "react-hot-toast";
import { decryptData } from "../../../services/crypto";

const Main = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem;
`;

const SearchContainer = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.brown}, ${colors.textBlue});
    z-index: 1;
  }
`;

const SearchHeader = styled.div`
  padding: 1rem 1.25rem 0.5rem 1.25rem;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, ${colors.white} 100%);
`;

const SearchTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.text};
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
`;

const SearchIcon = styled.span`
  font-size: 1.25rem;
  filter: grayscale(0.2);
`;

const SearchForm = styled.div`
  padding: 1rem 1.25rem 1.25rem 1.25rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const FormLabel = styled.label`
  color: ${colors.text};
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.1rem;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AutocompleteContainer = styled.div`
  width: 100%;
`;

const TextInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.text};
  font-size: 0.8rem;
  transition: all 0.2s ease;
  outline: none;
  height: 36px;
  
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
  }
  
  &::placeholder {
    color: ${colors.inputFeild};
    font-size: 0.75rem;
  }
  
  &:hover {
    border-color: ${colors.brown};
  }
`;

const SelectInput = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.text};
  font-size: 0.8rem;
  transition: all 0.2s ease;
  outline: none;
  cursor: pointer;
  height: 36px;
  
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
  }
  
  &:hover {
    border-color: ${colors.brown};
  }
  
  option {
    padding: 6px;
    font-size: 0.8rem;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${colors.inputBorderWhite};
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 20px;
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(36, 147, 147, 0.3);
  min-width: 140px;
  justify-content: center;
  height: 36px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(36, 147, 147, 0.4);
    background: linear-gradient(135deg, #1a6b6b 0%, ${colors.brown} 100%);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const getDefaultFilters = () => {
  return {
    month: "",
    year: "",
    employeeId: "",
    project: "",
    clientId: "",
    phoneNumber: "",
    leadId: "",
  };
};

export const CMPFirst = ({ onFilterChange }) => {
  // Get user role
  let role = null;
  const storedStaffRole = localStorage.getItem("role");
  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      console.error("Failed to decrypt role:", error);
    }
  }

  const isAdminOrCEO = role === "ceo" || role === "admin";

  // Get staff ID
  let staffId = null;
  const storedStaffId = localStorage.getItem("id");
  if (storedStaffId) {
    try {
      staffId = decryptData(storedStaffId);
    } catch (error) {
      console.error("Failed to decrypt staff ID:", error);
    }
  }

  const { data: hierarchyStaff } = useGetSingleHierarchyQuery({ staffId });
  const { data: staff } = useGetActiveStaffQuery();
  const { data: project } = useGetAllProjectsQuery();

  const now = new Date();
  const [filters, setFilters] = useState(getDefaultFilters);

  const { data: cmpClientsData, isFetching: isClientFetcing, isLoading: isClientLoading } = useGetCMPClientsQuery();

  const staffOptions = useMemo(() => {
    const options = [{ id: "", label: "All Employees" }];
    
    if (isAdminOrCEO && staff && Array.isArray(staff)) {
      staff.forEach((emp) => {
        if (emp?._id && emp?.firstName && emp?.lastName) {
          options.push({
            id: emp._id,
            label: `${emp.firstName} ${emp.lastName}`,
          });
        }
      });
    } else if (hierarchyStaff && Array.isArray(hierarchyStaff)) {
      hierarchyStaff.forEach((emp) => {
        if (emp?._id && emp?.firstName && emp?.lastName) {
          options.push({
            id: emp._id,
            label: `${emp.firstName} ${emp.lastName}`,
          });
        }
      });
    }
    
    return options;
  }, [hierarchyStaff, staff, isAdminOrCEO]);

  const clientOptions = useMemo(() => {
    const options = [{ id: "", label: "Select Client" }];
    
    const clients = cmpClientsData?.clients;
    if (clients && Array.isArray(clients)) {
      clients.forEach((client) => {
        if (client?.id && client?.label) {
          options.push({
            id: client.id,
            label: client.label,
          });
        }
      });
    }
    
    return options;
  }, [cmpClientsData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const monthOptions = [
    { value: "", label: "All Months" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handleChange = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  }, []);

  const handleSearch = useCallback(() => {
    if (onFilterChange && typeof onFilterChange === "function") {
      onFilterChange(filters);
      toast.success("Filters applied successfully!");
    }
  }, [filters, onFilterChange]);

  const handleClear = useCallback(() => {
    const clearedFilters = getDefaultFilters();
    setFilters(clearedFilters);
    if (onFilterChange && typeof onFilterChange === "function") {
      onFilterChange(clearedFilters);
    }
    toast.success("Filters cleared!");
  }, [onFilterChange]);

  // Initialize filters on mount
  useEffect(() => {
    onFilterChange(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Main>
        <SearchContainer>
          <SearchHeader>
            <SearchTitle>
              <SearchIcon>🔍</SearchIcon>
              Filter CMP Data
            </SearchTitle>
          </SearchHeader>
          
          <SearchForm>
            <FormRow>
              <FormGroup>
                <FormLabel>Month</FormLabel>
                <AutocompleteContainer>
                  <Autocomplete
                    id="month-autocomplete"
                    size="small"
                    options={monthOptions}
                    getOptionLabel={(option) => option.label}
                    value={monthOptions.find((m) => m.value === filters.month) || null}
                    onChange={(e, newValue) => {
                      const selectedMonth = newValue?.value ?? "";
                      const selectedYear = selectedMonth ? new Date().getFullYear() : "";
                      setFilters((prev) => ({
                        ...prev,
                        month: selectedMonth,
                        year: selectedYear,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Month"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: colors.white,
                            '& fieldset': {
                              borderColor: colors.inputBorderWhite,
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: colors.brown,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.brown,
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            padding: '8px 12px',
                            fontSize: '13px',
                          },
                        }}
                      />
                    )}
                  />
                </AutocompleteContainer>
              </FormGroup>

              {isAdminOrCEO && (
                <FormGroup>
                  <FormLabel>Employee</FormLabel>
                  <AutocompleteContainer>
                    <Autocomplete
                      id="employee-autocomplete"
                      size="small"
                      options={staffOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        staffOptions.find((s) => s.id === filters.employeeId) ||
                        staffOptions[0]
                      }
                      onChange={(e, newValue) => {
                        handleChange("employeeId", newValue?.id || "");
                        // Reset client when employee changes
                        setFilters((prev) => ({
                          ...prev,
                          employeeId: newValue?.id || "",
                          clientId: "",
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Employee"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: colors.white,
                              '& fieldset': {
                                borderColor: colors.inputBorderWhite,
                                borderWidth: '1px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.brown,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: colors.brown,
                                borderWidth: '2px',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '8px 12px',
                              fontSize: '13px',
                            },
                          }}
                        />
                      )}
                    />
                  </AutocompleteContainer>
                </FormGroup>
              )}

              <FormGroup>
                <FormLabel>Project</FormLabel>
                <SelectInput
                  name="project"
                  id="project"
                  value={filters.project}
                  onChange={handleInputChange}
                >
                  <option value="">Select Project</option>
                  {project && Array.isArray(project) && project.map((proj) => {
                    if (!proj?._id || !proj?.projectName) return null;
                    return (
                      <option key={proj._id} value={proj._id}>
                        {proj.projectName}
                      </option>
                    );
                  })}
                </SelectInput>
              </FormGroup>

              <FormGroup>
                <FormLabel>Client</FormLabel>
                <AutocompleteContainer>
                  <Autocomplete
                    id="client-autocomplete"
                    size="small"
                    disabled={isClientLoading || isClientFetcing}
                    options={clientOptions}
                    getOptionLabel={(option) => option.label}
                    onChange={(e, value) => {
                      handleChange("clientId", value?.id || "");
                    }}
                    value={
                      clientOptions.find((c) => c.id === filters.clientId) || null
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Client"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: colors.white,
                            '& fieldset': {
                              borderColor: colors.inputBorderWhite,
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: colors.brown,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.brown,
                              borderWidth: '2px',
                            },
                          },
                          '& .MuiInputBase-input': {
                            padding: '8px 12px',
                            fontSize: '13px',
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.label}
                      </Box>
                    )}
                  />
                </AutocompleteContainer>
              </FormGroup>

              <FormGroup>
                <FormLabel>Phone Number</FormLabel>
                <TextInput
                  type="tel"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={filters.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Lead ID</FormLabel>
                <TextInput
                  type="text"
                  name="leadId"
                  id="leadId"
                  value={filters.leadId}
                  onChange={handleInputChange}
                  placeholder="Enter Lead ID"
                />
              </FormGroup>
            </FormRow>
            
            <ActionRow>
              <SearchButton 
                onClick={handleClear}
                style={{
                  background: colors.inputWhite,
                  color: colors.textMed,
                  border: `1px solid ${colors.inputBorderWhite}`,
                  marginRight: '0.5rem'
                }}
              >
                Clear
              </SearchButton>
              <SearchButton onClick={handleSearch}>
                <SearchIcon>🔍</SearchIcon>
                Apply Filters
              </SearchButton>
            </ActionRow>
          </SearchForm>
        </SearchContainer>
      </Main>
      <Toaster />
    </>
  );
};
